import { GAME_CONFIG, clamp } from '../config/game-config'
import type { BobbleState, CollisionDamage } from './types'

export const healthRatio = (bobble: Pick<BobbleState, 'health' | 'maxHealth'>): number =>
  clamp(bobble.health / bobble.maxHealth, 0, 1)

export const healthColor = (bobble: Pick<BobbleState, 'health' | 'maxHealth'>): string => {
  const hue = Math.round(120 * healthRatio(bobble))
  return `hsl(${hue} 96% 58%)`
}

export const calculateScore = (bobbles: readonly BobbleState[]): number => {
  let squareHealth = 0
  let circleHealth = 0

  for (const bobble of bobbles) {
    if (!bobble.alive) continue
    if (bobble.kind === 'square') squareHealth += Math.max(0, bobble.health)
    else circleHealth += Math.max(0, bobble.health)
  }

  return (squareHealth - circleHealth) / 100
}

export interface HealthBaseline {
  square: number
  circle: number
}

export const calculateHealthBaseline = (bobbles: readonly BobbleState[]): HealthBaseline => {
  const result: HealthBaseline = { square: 0, circle: 0 }
  for (const bobble of bobbles) result[bobble.kind] += bobble.maxHealth
  return result
}

export const calculateRelativeBalance = (
  bobbles: readonly BobbleState[],
  baseline: HealthBaseline,
): number => {
  let squareHealth = 0
  let circleHealth = 0

  for (const bobble of bobbles) {
    if (!bobble.alive) continue
    if (bobble.kind === 'square') squareHealth += Math.max(0, bobble.health)
    else circleHealth += Math.max(0, bobble.health)
  }

  const squareRetention = baseline.square > 0 ? squareHealth / baseline.square : 0
  const circleRetention = baseline.circle > 0 ? circleHealth / baseline.circle : 0
  return clamp((squareRetention - circleRetention) * 100, -100, 100)
}

export const balanceTargetForLevel = (level: number): number =>
  Math.min(
    GAME_CONFIG.maximumBalanceTarget,
    GAME_CONFIG.firstLevelBalanceTarget + Math.max(0, level - 1) * GAME_CONFIG.balanceTargetStep,
  )

export const calculateCollisionDamage = (normalSpeed: number): CollisionDamage => ({
  speed: Math.abs(normalSpeed),
  damage: Math.max(
    0,
    (Math.abs(normalSpeed) - GAME_CONFIG.collisionSpeedThreshold) *
      GAME_CONFIG.collisionDamageScale,
  ),
})

export const applyDamage = (bobble: BobbleState, damage: number): boolean => {
  if (!bobble.alive || damage <= 0) return false

  bobble.health = Math.max(GAME_CONFIG.deathHealth, bobble.health - damage)
  if (bobble.health <= GAME_CONFIG.deathHealth) {
    bobble.alive = false
    return true
  }

  return false
}
